import { authHeaders, getAuthBaseUrl } from './authApi';

// ─── Report moderation REST client ───────────────────────────────────────────
// Mirrors pvpCampaignApi.js. All calls hit /api/admin/reports* and require an
// admin JWT (Authorization: Bearer <token>).

export async function fetchAdminReports({ status = null, category = null, q = null } = {}) {
  const base = getAuthBaseUrl();
  if (!base) return [];
  const params = new URLSearchParams();
  if (status && status !== 'all') params.set('status', status);
  if (category && category !== 'all') params.set('category', category);
  if (q && q.trim()) params.set('q', q.trim());
  const qs = params.toString();
  const res = await fetch(`${base}/api/admin/reports${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !Array.isArray(data.reports)) {
    throw new Error(data.error || 'Failed to load reports');
  }
  return data.reports;
}

export async function fetchAdminReportStats() {
  const base = getAuthBaseUrl();
  if (!base) return null;
  const res = await fetch(`${base}/api/admin/reports/stats`, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.stats) throw new Error(data.error || 'Failed to load stats');
  return data.stats;
}

export async function fetchAdminReport(reportId) {
  const base = getAuthBaseUrl();
  if (!base) return null;
  const res = await fetch(`${base}/api/admin/reports/${encodeURIComponent(reportId)}`, {
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.report) throw new Error(data.error || 'Report not found');
  return data.report;
}

export async function resolveAdminReport(reportId, { outcome, banDuration = null, detail = null }) {
  const base = getAuthBaseUrl();
  if (!base) return { success: false, error: 'API unavailable' };
  const res = await fetch(`${base}/api/admin/reports/${encodeURIComponent(reportId)}/resolve`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ outcome, banDuration, detail }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    return { success: false, error: data.error || 'Resolve failed' };
  }
  return { success: true, status: data.status };
}

export async function fetchAdminAppeals({ status = null } = {}) {
  const base = getAuthBaseUrl();
  if (!base) return [];
  const qs = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  const res = await fetch(`${base}/api/admin/report-appeals${qs}`, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !Array.isArray(data.appeals)) {
    throw new Error(data.error || 'Failed to load appeals');
  }
  return data.appeals;
}

export async function resolveAdminAppeal(appealId, { decision, note = null, restoreCredit = 0 }) {
  const base = getAuthBaseUrl();
  if (!base) return { success: false, error: 'API unavailable' };
  const res = await fetch(`${base}/api/admin/report-appeals/${encodeURIComponent(appealId)}/resolve`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ decision, note, restoreCredit }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    return { success: false, error: data.error || 'Resolve failed' };
  }
  return { success: true, decision: data.decision };
}

export async function liftUserBan(userId, { restoreCredit = 0, note = null, reportId = null } = {}) {
  const base = getAuthBaseUrl();
  if (!base) return { success: false, error: 'API unavailable' };
  const res = await fetch(`${base}/api/admin/users/${encodeURIComponent(userId)}/lift-ban`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ restoreCredit, note, reportId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    return { success: false, error: data.error || 'Lift ban failed' };
  }
  return { success: true };
}

// ─── Display helpers ─────────────────────────────────────────────────────────

export const REPORT_CATEGORIES = [
  { value: 'harassment', label: '骚扰/辱骂', penalty: 10 },
  { value: 'dangerous_burial', label: '危险埋藏', penalty: 15 },
  { value: 'fake_info', label: '虚假信息', penalty: 8 },
  { value: 'fake_event', label: '虚假活动', penalty: 8 },
  { value: 'fee_abuse', label: '费用滥用', penalty: 5 },
  { value: 'spam', label: '垃圾信息', penalty: 5 },
  { value: 'no_show', label: '爽约未到', penalty: 3 },
  { value: 'other', label: '其他', penalty: 3 },
];

export const REPORT_OUTCOMES = [
  { value: 'success_normal', label: '举报成立 · 正常处罚', tone: 'confirm' },
  { value: 'success_max', label: '举报成立 · 顶格处理（封号）', tone: 'danger' },
  { value: 'reject_more_evidence', label: '要求举报人补充证据', tone: 'neutral' },
  { value: 'reject_await_response', label: '等待被举报人回应', tone: 'neutral' },
  { value: 'reject_failed', label: '举报不成立 · 关闭', tone: 'neutral' },
];

export const BAN_DURATIONS = [
  { value: '1m', label: '封号 1 个月' },
  { value: '3m', label: '封号 3 个月' },
  { value: '6m', label: '封号 6 个月' },
  { value: '1y', label: '封号 1 年' },
  { value: 'permanent', label: '永久封号' },
];

export function categoryLabel(value) {
  return REPORT_CATEGORIES.find((c) => c.value === value)?.label || value || '—';
}

export function categoryPenalty(value) {
  return REPORT_CATEGORIES.find((c) => c.value === value)?.penalty ?? 3;
}

export function reportStatusLabel(status) {
  switch (status) {
    case 'pending': return '待处理';
    case 'confirmed': return '已处罚';
    case 'rejected': return '已驳回';
    case 'awaiting_evidence': return '待补充证据';
    case 'awaiting_response': return '待被举报人回应';
    default: return status || '—';
  }
}

export function appealStatusLabel(status) {
  switch (status) {
    case 'pending': return '待审核';
    case 'accepted': return '已采纳';
    case 'rejected': return '已驳回';
    default: return status || '—';
  }
}

export function badgeStatus(reportStatus) {
  if (reportStatus === 'pending') return 'active';
  if (reportStatus === 'confirmed' || reportStatus === 'rejected') return 'ended';
  return 'draft';
}

export function formatIsoDate(iso, lang = 'zh') {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(lang === 'en' ? 'en-AU' : 'zh-CN', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}
